/**
 * Leaderboard Page - Top performers tracked in real-time
 *
 * Premium design with micro-animations and interactions:
 * - Staggered card entrance animations
 * - Hover effects with glow and scale
 * - Animated rank badges with shine effects
 * - Metric toggle with satisfying feedback
 * - Animated number counters
 * - Trophy/medal animations
 *
 * Refactored to use DashboardShell widget system while keeping the
 * unique custom animated ranking UI as header content.
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { FilterBar } from "../components";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { useDateRange } from "../context/DateRangeContext";
import { useActiveTheme, useTheme } from "../context/ThemeContext";
import { useFilters } from "../context/FilterContext";
import { DashboardDataProvider } from "../context/DashboardContext";
import { DashboardShell } from "../components/dashboard/DashboardShell";
import { LEADERBOARD_DEFAULT } from "../components/dashboard/defaults";
import type { DashboardData } from "../components/dashboard/types";

// Types
interface OfferBreakdown {
  offer: string;
  totalCashCollected: number;
  orderCount: number;
}

interface CloserSummary {
  id: string;
  closerName: string;
  totalCashCollected: number;
  totalRevenueGenerated: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgOrderValue: number;
  callCount: number;
  callsClosed: number;
  orderCount: number;
  closeEligibleCount: number;
  closeRate: number;
  cashByOffer: OfferBreakdown[];
}

interface SetterSummary {
  id: string;
  setterName: string;
  totalCashCollected: number;
  avgCashPerCall: number;
  callCount: number;
  callsDue: number;
  showRate: number;
  orderCount: number;
  cashByOffer: OfferBreakdown[];
}

interface CashByOfferSummary {
  id: string;
  name: string;
  title: string;
  description: string;
  totalCashCollected: number;
  orderCount: number;
  cashByOffer: OfferBreakdown[];
}

interface FilterOption {
  value: string;
  label: string;
}

interface FilterOptions {
  platforms: FilterOption[];
  coaches: FilterOption[];
  closers: FilterOption[];
  setters: FilterOption[];
  contents: FilterOption[];
  offers: FilterOption[];
}

// Helper to normalize filter options that may be string[] or FilterOption[]
function normalizeToFilterOptions(data: unknown): FilterOption[] {
  if (!Array.isArray(data)) return [];
  return data.map((item: unknown) => {
    if (typeof item === "string") {
      return { value: item, label: item };
    }
    if (item && typeof item === "object" && "value" in item) {
      const obj = item as { value: string; label?: string };
      return { value: obj.value, label: obj.label || obj.value };
    }
    return { value: String(item), label: String(item) };
  });
}

// Raw API response may have string[] or FilterOption[] for filter options
interface RawFilterOptions {
  platforms: (string | FilterOption)[];
  coaches: (string | FilterOption)[];
  closers: (string | FilterOption)[];
  setters: (string | FilterOption)[];
  contents: (string | FilterOption)[];
  offers: (string | FilterOption)[];
}

interface LeaderboardData {
  topClosers: CloserSummary[];
  topSetters: SetterSummary[];
  filterOptions: RawFilterOptions;
}

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Metric toggle types
type CloserMetric = "cash" | "aov" | "cashPerCall" | "closeRate";
type SetterMetric = "cash" | "cashPerCall" | "calls" | "showRate";

// Animated Crown Icon with shimmer
function CrownIcon({ className }: { className?: string }) {
  return (
    <div className={`relative ${className}`}>
      <svg viewBox="0 0 24 24" fill="currentColor" className="relative z-10">
        <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z" />
      </svg>
      {/* Shimmer overlay */}
      <div
        className="absolute inset-0 z-20 opacity-60"
        style={{
          background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.4) 50%, transparent 60%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 3s infinite linear",
        }}
      />
    </div>
  );
}

// Trophy icon with bounce animation
function TrophyIcon({ color, isHovered }: { color: string; isHovered: boolean }) {
  return (
    <div
      className="transition-transform duration-300"
      style={{
        transform: isHovered ? "scale(1.2) rotate(-5deg)" : "scale(1)",
      }}
    >
      <svg className="w-6 h-6" viewBox="0 0 24 24" fill={color}>
        <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z">
          <animate
            attributeName="opacity"
            values={isHovered ? "1;0.7;1" : "1"}
            dur="0.5s"
            repeatCount={isHovered ? "indefinite" : "0"}
          />
        </path>
      </svg>
    </div>
  );
}

// Metric toggle button with ripple effect
function MetricButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  const [ripple, setRipple] = useState<{ x: number; y: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setRipple({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
        setTimeout(() => setRipple(null), 500);
      }
      onClick();
    },
    [onClick]
  );

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={handleClick}
      className={`
        relative overflow-hidden px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide
        transition-all duration-200 ease-out
        ${
          active
            ? "bg-[var(--brand-primary)] text-white shadow-lg shadow-[var(--brand-primary)]/30"
            : "bg-secondary text-muted-foreground hover:bg-secondary/80 hover:text-foreground"
        }
      `}
      style={{
        transform: active ? "scale(1.02)" : "scale(1)",
      }}
    >
      <span className="relative z-10">{label}</span>

      {/* Active indicator glow */}
      {active && (
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: "radial-gradient(circle at center, var(--brand-primary) 0%, transparent 70%)",
          }}
        />
      )}

      {/* Ripple effect */}
      {ripple && (
        <span
          className="absolute rounded-full bg-white/30 animate-ping"
          style={{
            left: ripple.x - 10,
            top: ripple.y - 10,
            width: 20,
            height: 20,
          }}
        />
      )}
    </button>
  );
}

// Animated rank badge with shine effect
function RankBadge({ rank, isHovered }: { rank: number; isHovered: boolean }) {
  const colors = {
    1: { bg: "linear-gradient(135deg, #FFD700 0%, #FFA500 50%, #FFD700 100%)", text: "#1a1a1a", glow: "#FFD700" },
    2: { bg: "linear-gradient(135deg, #E8E8E8 0%, #B8B8B8 50%, #E8E8E8 100%)", text: "#1a1a1a", glow: "#C0C0C0" },
    3: { bg: "linear-gradient(135deg, #CD7F32 0%, #8B5A2B 50%, #CD7F32 100%)", text: "#fff", glow: "#CD7F32" },
  };

  const config = colors[rank as keyof typeof colors] || { bg: "rgba(255,255,255,0.1)", text: "#888", glow: "transparent" };

  return (
    <div
      className="relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-lg transition-all duration-300"
      style={{
        background: config.bg,
        color: config.text,
        boxShadow: isHovered && rank <= 3 ? `0 0 20px ${config.glow}60, 0 0 40px ${config.glow}30` : "none",
        transform: isHovered ? "scale(1.1)" : "scale(1)",
      }}
    >
      {/* Shine overlay for top 3 */}
      {rank <= 3 && (
        <div
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            background: "linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.5) 50%, transparent 60%)",
            backgroundSize: "200% 100%",
            animation: isHovered ? "shimmer 1s infinite linear" : "none",
          }}
        />
      )}
      <span className="relative z-10">{rank}</span>
    </div>
  );
}

// Enhanced ranking card with animations
function RankingCard({
  rank,
  name,
  subtitle,
  primaryValue,
  secondaryValue,
  index,
  isVisible,
  onClick,
  callToAction,
}: {
  rank: number;
  name: string;
  subtitle: string;
  primaryValue: string;
  secondaryValue?: string;
  index: number;
  isVisible: boolean;
  onClick?: () => void;
  callToAction?: string;
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getMedalColor = () => {
    if (rank === 1) return "#FFD700";
    if (rank === 2) return "#C0C0C0";
    if (rank === 3) return "#CD7F32";
    return "transparent";
  };

  const getCardGradient = () => {
    if (rank === 1) return "linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,165,0,0.05) 100%)";
    if (rank === 2) return "linear-gradient(135deg, rgba(192,192,192,0.12) 0%, rgba(128,128,128,0.03) 100%)";
    if (rank === 3) return "linear-gradient(135deg, rgba(205,127,50,0.12) 0%, rgba(139,90,43,0.03) 100%)";
    return "transparent";
  };

  const getBorderColor = () => {
    if (isHovered && rank === 1) return "rgba(255,215,0,0.6)";
    if (isHovered && rank === 2) return "rgba(192,192,192,0.5)";
    if (isHovered && rank === 3) return "rgba(205,127,50,0.5)";
    if (rank <= 3) return "rgba(255,255,255,0.1)";
    return "rgba(255,255,255,0.05)";
  };

  const sharedClassName = `relative rounded-xl p-3 sm:p-4 transition-all duration-300 ${
    onClick ? "w-full text-left cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent" : "cursor-default"
  }`;

  const sharedStyle = {
    background: getCardGradient(),
    border: `1px solid ${getBorderColor()}`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible
      ? isHovered
        ? "translateY(0) scale(1)"
        : "translateY(0) scale(1)"
      : "translateY(20px) scale(0.95)",
    transitionDelay: isVisible ? `${index * 60}ms` : "0ms",
    boxShadow: isHovered
      ? `0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px ${getBorderColor()}`
      : "0 2px 8px rgba(0,0,0,0.1)",
  } as const;

  const cardBody = (
    <>
      {/* Glow effect for top 3 on hover */}
      {isHovered && rank <= 3 && (
        <div
          className="absolute inset-0 rounded-xl opacity-20 pointer-events-none"
          style={{
            background: `radial-gradient(circle at center, ${getMedalColor()} 0%, transparent 70%)`,
          }}
        />
      )}

      <div className="relative flex items-center gap-2 sm:gap-4">
        <RankBadge rank={rank} isHovered={isHovered} />

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm sm:text-base text-foreground truncate tracking-tight">{name}</p>
          <p className="text-xs sm:text-sm text-muted-foreground truncate">{subtitle}</p>
          {callToAction && (
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--brand-primary)]/80">
              {callToAction}
            </p>
          )}
        </div>

        <div className="text-right flex items-center gap-2 sm:gap-3">
          <div>
            <p
              className="font-bold text-sm sm:text-lg tracking-tight"
              style={{ color: "var(--brand-primary)" }}
            >
              {primaryValue}
            </p>
            {secondaryValue && (
              <p className="text-xs text-muted-foreground">{secondaryValue}</p>
            )}
          </div>

          {rank <= 3 && <TrophyIcon color={getMedalColor()} isHovered={isHovered} />}
        </div>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={sharedClassName}
        style={sharedStyle}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {cardBody}
      </button>
    );
  }

  return (
    <div
      className={sharedClassName}
      style={sharedStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {cardBody}
    </div>
  );
}

function CashByOfferModal({
  summary,
  open,
  onOpenChange,
}: {
  summary: CashByOfferSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { theme } = useTheme();
  const activeTheme = useActiveTheme();
  const [selectedOffer, setSelectedOffer] = useState<string>("all");
  const offerBreakdown = summary?.cashByOffer || [];
  const filteredOfferBreakdown = selectedOffer === "all"
    ? offerBreakdown
    : offerBreakdown.filter((offer) => offer.offer === selectedOffer);
  const selectedOfferMetrics = selectedOffer === "all"
    ? null
    : offerBreakdown.find((offer) => offer.offer === selectedOffer) || null;
  const maxOfferCash = filteredOfferBreakdown.reduce((max, offer) => Math.max(max, offer.totalCashCollected), 0);
  const displayCash = selectedOfferMetrics?.totalCashCollected ?? summary?.totalCashCollected ?? 0;
  const displayOrders = selectedOfferMetrics?.orderCount ?? summary?.orderCount ?? 0;
  const displayAvgOrderValue = displayOrders > 0 ? displayCash / displayOrders : 0;
  const isDarkMode = theme === "dark" || theme === "orange";
  const modalShadow = isDarkMode ? activeTheme.cardStyle.shadow.dark : activeTheme.cardStyle.shadow.light;
  const modalRadius = activeTheme.cardStyle.borderRadius;
  const statRadius = `calc(${activeTheme.components.baseRadius} + 4px)`;
  // Use OPAQUE backgrounds so the dialog is visible over the dark overlay
  const panelBackground = isDarkMode
    ? "linear-gradient(180deg, #1a0f08 0%, #241208 100%)"
    : "linear-gradient(180deg, #ffffff 0%, #fef7f0 100%)";
  const innerPanelBackground = isDarkMode
    ? "linear-gradient(180deg, #2a1a10 0%, #1e1008 100%)"
    : "linear-gradient(180deg, #fef7f0 0%, #fdf0e6 100%)";

  useEffect(() => {
    setSelectedOffer("all");
  }, [summary?.id, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border border-border/50 text-foreground w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[85vh] overflow-y-auto p-4 sm:p-6"
        style={{
          borderRadius: modalRadius,
          boxShadow: modalShadow,
          background: panelBackground,
        }}
      >
        {summary && (
          <>
            <div
              className="pointer-events-none absolute inset-0 rounded-[inherit]"
              style={{
                opacity: isDarkMode ? 0.5 : 0.3,
                background: "radial-gradient(circle at top left, rgba(240,73,17,0.25) 0%, transparent 42%), radial-gradient(circle at bottom right, rgba(208,61,14,0.2) 0%, transparent 36%)",
              }}
            />

            <DialogHeader className="relative space-y-2 sm:space-y-3 pr-8 text-left">
              <div
                className="inline-flex w-fit items-center px-2 sm:px-3 py-1 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.22em]"
                style={{
                  borderRadius: activeTheme.components.badgeRadius,
                  color: "#F04911",
                  border: isDarkMode ? "1px solid rgba(240,73,17,0.35)" : "1px solid rgba(240,73,17,0.25)",
                  background: isDarkMode ? "rgba(240,73,17,0.15)" : "rgba(240,73,17,0.08)",
                }}
              >
                {summary.title}
              </div>
              <DialogTitle
                className="text-xl sm:text-2xl tracking-tight text-foreground"
                style={{
                  fontSize: activeTheme.typography.pageTitle.fontSize,
                  fontWeight: activeTheme.typography.pageTitle.fontWeight,
                  letterSpacing: activeTheme.typography.pageTitle.letterSpacing,
                }}
              >
                {summary.name}
              </DialogTitle>
              <DialogDescription className="max-w-xl text-sm text-muted-foreground">
                {summary.description}
              </DialogDescription>
            </DialogHeader>

            <div className="relative grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                { label: "Total Cash", value: formatCurrency(displayCash), highlight: true },
                { label: "Orders", value: String(displayOrders), highlight: false },
                { label: "Avg Per Order", value: formatCurrency(displayAvgOrderValue), highlight: false },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl border p-3 sm:p-4"
                  style={{
                    borderColor: isDarkMode ? "rgba(240,73,17,0.2)" : "rgba(240,73,17,0.15)",
                    background: innerPanelBackground,
                  }}
                >
                  <p className="text-xs font-semibold uppercase tracking-[0.2em]" style={{ color: isDarkMode ? "rgba(255,255,255,0.6)" : "#8a6545" }}>{stat.label}</p>
                  <p className={`mt-1 sm:mt-2 text-xl sm:text-2xl font-black tracking-tight ${stat.highlight ? "text-[#F04911]" : ""}`} style={stat.highlight ? {} : { color: isDarkMode ? "#f5eae0" : "#1a0f05" }}>
                    {stat.value}
                  </p>
                </div>
              ))}
            </div>

            <div className="relative space-y-3">
              {offerBreakdown.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {["all", ...offerBreakdown.map((offer) => offer.offer)].map((offerName) => {
                    const isActive = selectedOffer === offerName;
                    return (
                      <button
                        key={offerName}
                        type="button"
                        onClick={() => setSelectedOffer(offerName)}
                        className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all duration-200"
                        style={{
                          color: isActive ? "#ffffff" : (isDarkMode ? "rgba(255,255,255,0.6)" : "#8a6545"),
                          background: isActive
                            ? "linear-gradient(135deg, #F04911 0%, #D03D0E 100%)"
                            : (isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(240,73,17,0.06)"),
                          border: isActive
                            ? "1px solid transparent"
                            : (isDarkMode ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(240,73,17,0.15)"),
                        }}
                      >
                        {offerName === "all" ? "All Offers" : offerName}
                      </button>
                    );
                  })}
                </div>
              )}

              {offerBreakdown.length === 0 ? (
                <div
                  className="border border-dashed rounded-xl px-5 py-8 text-center text-sm"
                  style={{
                    borderColor: isDarkMode ? "rgba(255,255,255,0.15)" : "rgba(240,73,17,0.2)",
                    background: isDarkMode ? "rgba(255,255,255,0.04)" : "rgba(240,73,17,0.03)",
                    color: isDarkMode ? "rgba(255,255,255,0.5)" : "#8a6545",
                  }}
                >
                  No offer-level cash found for the current leaderboard filters.
                </div>
              ) : (
                filteredOfferBreakdown.map((offer) => {
                  const shareBase = selectedOfferMetrics?.totalCashCollected ?? summary.totalCashCollected;
                  const share = shareBase > 0 ? (offer.totalCashCollected / shareBase) * 100 : 0;
                  const width = maxOfferCash > 0 ? Math.max((offer.totalCashCollected / maxOfferCash) * 100, 6) : 0;

                  return (
                    <div
                      key={offer.offer}
                      className="border rounded-xl p-3 sm:p-4 transition-colors duration-200"
                      style={{
                        borderColor: isDarkMode ? "rgba(240,73,17,0.2)" : "rgba(240,73,17,0.15)",
                        background: innerPanelBackground,
                      }}
                    >
                      <div className="flex items-start justify-between gap-3 sm:gap-4">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold tracking-tight" style={{ color: isDarkMode ? "#f5eae0" : "#1a0f05" }}>{offer.offer}</p>
                          <p className="mt-1 text-xs" style={{ color: isDarkMode ? "rgba(255,255,255,0.5)" : "#8a6545" }}>
                            {offer.orderCount} {offer.orderCount === 1 ? "order" : "orders"}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-base sm:text-lg font-bold tracking-tight text-[#F04911]">
                            {formatCurrency(offer.totalCashCollected)}
                          </p>
                          <p className="mt-1 text-xs" style={{ color: isDarkMode ? "rgba(255,255,255,0.5)" : "#8a6545" }}>
                            {selectedOffer === "all" ? `${share.toFixed(1)}% of total` : "Selected offer"}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 h-2 overflow-hidden rounded-full" style={{ background: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(240,73,17,0.1)" }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${width}%`,
                            background: "linear-gradient(90deg, #F04911 0%, #D03D0E 100%)",
                          }}
                        />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Premium loading skeleton
function LoadingSkeleton() {
  return (
    <div className="w-full min-w-0 space-y-8">
      {/* Header skeleton */}
      <div className="text-center py-4 sm:py-6 md:py-12">
        <div
          className="w-20 h-20 mx-auto rounded-2xl mb-6"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
          }}
        />
        <div
          className="h-12 w-72 mx-auto rounded-lg mb-3"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
          }}
        />
        <div
          className="h-4 w-56 mx-auto rounded"
          style={{
            background: "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.07) 50%, rgba(255,255,255,0.03) 100%)",
            backgroundSize: "200% 100%",
            animation: "shimmer 1.5s infinite linear",
            animationDelay: "0.1s",
          }}
        />
      </div>

      {/* Filter bar skeleton */}
      <div
        className="h-16 rounded-xl"
        style={{
          background: "linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 100%)",
          backgroundSize: "200% 100%",
          animation: "shimmer 1.5s infinite linear",
        }}
      />

      {/* Cards skeleton */}
      <div className="grid gap-6 lg:grid-cols-2">
        {[0, 1].map((col) => (
          <div key={col} className="rounded-2xl border border-white/5 p-6 bg-white/[0.02]">
            <div
              className="h-8 w-48 rounded-lg mb-6"
              style={{
                background: "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)",
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite linear",
              }}
            />
            <div className="space-y-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-16 rounded-xl"
                  style={{
                    background: "linear-gradient(90deg, rgba(255,255,255,0.02) 0%, rgba(255,255,255,0.05) 50%, rgba(255,255,255,0.02) 100%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s infinite linear",
                    animationDelay: `${i * 0.1}s`,
                  }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Section header with animated gradient
function SectionHeader({
  icon,
  title,
  iconColor,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  iconColor: string;
  children: React.ReactNode;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="relative p-3 sm:p-5 border-b border-white/5 overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Animated gradient background */}
      <div
        className="absolute inset-0 opacity-30 transition-opacity duration-500"
        style={{
          background: `linear-gradient(135deg, ${iconColor}20 0%, transparent 60%)`,
          opacity: isHovered ? 0.4 : 0.2,
        }}
      />

      <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <div
            className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center transition-all duration-300"
            style={{
              background: `linear-gradient(135deg, ${iconColor}30 0%, ${iconColor}10 100%)`,
              boxShadow: isHovered ? `0 0 20px ${iconColor}30` : "none",
              transform: isHovered ? "scale(1.05)" : "scale(1)",
            }}
          >
            {icon}
          </div>
          <h3 className="text-base sm:text-lg font-bold text-foreground tracking-tight">{title}</h3>
        </div>

        <div className="flex flex-wrap gap-1.5">{children}</div>
      </div>
    </div>
  );
}

export function Leaderboard() {
  // Use global date range from context (synced with header date picker)
  const { dateRange: globalDateRange, formatForApi } = useDateRange();

  // Use shared filters from context (synced with URL via nuqs)
  const {
    dateField,
    setDateField,
    platforms,
    setPlatforms,
    coaches,
    setCoaches,
    closers,
    setClosers,
    setters,
    setSetters,
    contents,
    setContents,
    offer,
    offers,
    setOffers,
    clearAllFilters,
  } = useFilters();

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<LeaderboardData | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectedCloserId, setSelectedCloserId] = useState<string | null>(null);
  const [selectedSetterId, setSelectedSetterId] = useState<string | null>(null);

  // Metric toggle state
  const [closerMetric, setCloserMetric] = useState<CloserMetric>("cash");
  const [setterMetric, setSetterMetric] = useState<SetterMetric>("cash");

  // Trigger visibility animation after data loads
  useEffect(() => {
    if (data && !isLoading) {
      setIsVisible(false);
      const timer = setTimeout(() => setIsVisible(true), 50);
      return () => clearTimeout(timer);
    }
  }, [data, isLoading, closerMetric, setterMetric]);

  // Fetch data
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Use global date range from context
      const apiDates = formatForApi();
      const params = new URLSearchParams();
      params.set("dateFrom", apiDates.startDate);
      params.set("dateTo", apiDates.endDate);
      params.set("dateField", dateField);
      platforms.forEach((p) => params.append("platform", p));
      coaches.forEach((c) => params.append("coach", c));
      closers.forEach((c) => params.append("closer", c));
      setters.forEach((s) => params.append("setter", s));
      contents.forEach((c) => params.append("content", c));
      if (offer && offer !== "all") params.set("offer", offer);
      const selectedOffers = offers.filter((o: string) => o !== "all");
      if (offer === "all" && selectedOffers.length > 0) params.set("offer", selectedOffers.join(","));

      const response = await fetch(`/api/leaderboard?${params.toString()}`);
      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to fetch leaderboard data");
      }

      setData(result.data);
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [formatForApi, dateField, platforms, coaches, closers, setters, contents, offer, offers]);

  // Fetch data when filters change (using global date range and shared filters)
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    if (!data?.filterOptions) return;
    const opts = data.filterOptions;

    // Helper to extract values from mixed string[] | FilterOption[] arrays
    const getValues = (arr: unknown): string[] => {
      if (!Array.isArray(arr)) return [];
      return arr.map((item: unknown) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "value" in item) {
          return (item as { value: string }).value;
        }
        return String(item);
      });
    };

    const platformValues = getValues(opts.platforms);
    const coachValues = getValues(opts.coaches);
    const closerValues = getValues(opts.closers);
    const setterValues = getValues(opts.setters);
    const contentValues = getValues(opts.contents);

    // Skip if filterOptions haven't loaded yet
    if (!platformValues.length && !coachValues.length && !closerValues.length) {
      return;
    }

    // Check platform filter
    if (platforms.length > 0 && platformValues.length > 0) {
      const validPlatforms = platforms.filter(p => platformValues.includes(p));
      if (validPlatforms.length !== platforms.length) {
        setPlatforms(validPlatforms);
      }
    }

    // Check coach filter
    if (coaches.length > 0 && coachValues.length > 0) {
      const validCoaches = coaches.filter(c => coachValues.includes(c));
      if (validCoaches.length !== coaches.length) {
        setCoaches(validCoaches);
      }
    }

    // Check closer filter
    if (closers.length > 0 && closerValues.length > 0) {
      const validClosers = closers.filter(c => closerValues.includes(c));
      if (validClosers.length !== closers.length) {
        setClosers(validClosers);
      }
    }

    // Check setter filter
    if (setters.length > 0 && setterValues.length > 0) {
      const validSetters = setters.filter(s => setterValues.includes(s));
      if (validSetters.length !== setters.length) {
        setSetters(validSetters);
      }
    }

    // Check content filter
    if (contents.length > 0 && contentValues.length > 0) {
      const validContents = contents.filter(c => contentValues.includes(c));
      if (validContents.length !== contents.length) {
        setContents(validContents);
      }
    }
  }, [data?.filterOptions, platforms, coaches, closers, setters, contents, setPlatforms, setCoaches, setClosers, setSetters, setContents]);

  // Sort closers based on selected metric
  const sortedClosers = useMemo(() => {
    if (!data?.topClosers) return [];
    const sorted = [...data.topClosers];

    switch (closerMetric) {
      case "cash":
        return sorted.sort((a, b) => b.totalCashCollected - a.totalCashCollected);
      case "aov":
        return sorted.sort((a, b) => b.avgOrderValue - a.avgOrderValue);
      case "cashPerCall":
        return sorted.sort((a, b) => b.avgCashPerCall - a.avgCashPerCall);
      case "closeRate":
        return sorted.sort((a, b) => b.closeRate - a.closeRate);
      default:
        return sorted;
    }
  }, [data?.topClosers, closerMetric]);

  // Sort setters based on selected metric
  const sortedSetters = useMemo(() => {
    if (!data?.topSetters) return [];
    const sorted = [...data.topSetters];

    switch (setterMetric) {
      case "cash":
        return sorted.sort((a, b) => b.totalCashCollected - a.totalCashCollected);
      case "cashPerCall":
        return sorted.sort((a, b) => b.avgCashPerCall - a.avgCashPerCall);
      case "calls":
        return sorted.sort((a, b) => b.callCount - a.callCount);
      case "showRate":
        return sorted.sort((a, b) => b.showRate - a.showRate);
      default:
        return sorted;
    }
  }, [data?.topSetters, setterMetric]);

  const selectedCloser = useMemo(
    () => sortedClosers.find((closer) => closer.id === selectedCloserId) || null,
    [selectedCloserId, sortedClosers]
  );

  const selectedSetter = useMemo(
    () => sortedSetters.find((setter) => setter.id === selectedSetterId) || null,
    [selectedSetterId, sortedSetters]
  );

  useEffect(() => {
    if (selectedCloserId && !selectedCloser) {
      setSelectedCloserId(null);
    }
  }, [selectedCloserId, selectedCloser]);

  useEffect(() => {
    if (selectedSetterId && !selectedSetter) {
      setSelectedSetterId(null);
    }
  }, [selectedSetterId, selectedSetter]);

  const selectedCashByOffer = useMemo<CashByOfferSummary | null>(() => {
    if (selectedCloser) {
      return {
        id: selectedCloser.id,
        name: selectedCloser.closerName,
        title: "Closer Offer Mix",
        description: "Offer-level cash collected for the current closer leaderboard filters.",
        totalCashCollected: selectedCloser.totalCashCollected,
        orderCount: selectedCloser.orderCount,
        cashByOffer: selectedCloser.cashByOffer,
      };
    }

    if (selectedSetter) {
      return {
        id: selectedSetter.id,
        name: selectedSetter.setterName,
        title: "Setter Offer Mix",
        description: "Offer-level cash influenced by the current setter leaderboard filters.",
        totalCashCollected: selectedSetter.totalCashCollected,
        orderCount: selectedSetter.orderCount,
        cashByOffer: selectedSetter.cashByOffer,
      };
    }

    return null;
  }, [selectedCloser, selectedSetter]);

  // Get display values
  const getCloserPrimaryValue = (closer: CloserSummary): string => {
    switch (closerMetric) {
      case "cash":
        return formatCurrency(closer.totalCashCollected);
      case "aov":
        return formatCurrency(closer.avgOrderValue);
      case "cashPerCall":
        return formatCurrency(closer.avgCashPerCall);
      case "closeRate":
        return formatPercent(closer.closeRate);
      default:
        return formatCurrency(closer.totalCashCollected);
    }
  };

  const getCloserSubtitle = (closer: CloserSummary): string => {
    switch (closerMetric) {
      case "cash":
        return `${closer.callsClosed} closes / ${closer.callCount} calls`;
      case "aov":
        return `${closer.orderCount} orders`;
      case "cashPerCall":
        return `${closer.callCount} calls`;
      case "closeRate":
        return `${closer.callsClosed}/${closer.closeEligibleCount} closed`;
      default:
        return `${closer.callsClosed} closes / ${closer.callCount} calls`;
    }
  };

  const getSetterPrimaryValue = (setter: SetterSummary): string => {
    switch (setterMetric) {
      case "cash":
        return formatCurrency(setter.totalCashCollected);
      case "cashPerCall":
        return formatCurrency(setter.avgCashPerCall);
      case "calls":
        return `${setter.callCount} calls`;
      case "showRate":
        return formatPercent(setter.showRate);
      default:
        return formatCurrency(setter.totalCashCollected);
    }
  };

  const getSetterSubtitle = (setter: SetterSummary): string => {
    switch (setterMetric) {
      case "cash":
        return `${setter.callCount} calls`;
      case "cashPerCall":
        return `${setter.callCount} calls`;
      case "calls":
        return `${setter.showRate.toFixed(0)}% show rate`;
      case "showRate":
        return `${setter.callCount}/${setter.callsDue} showed`;
      default:
        return `${setter.callCount} calls`;
    }
  };

  // Build filter options - normalize to handle both string[] and FilterOption[] from API
  const filterOpts: FilterOptions = data?.filterOptions
    ? {
        platforms: normalizeToFilterOptions(data.filterOptions.platforms),
        coaches: normalizeToFilterOptions(data.filterOptions.coaches),
        closers: normalizeToFilterOptions(data.filterOptions.closers),
        setters: normalizeToFilterOptions(data.filterOptions.setters),
        contents: normalizeToFilterOptions(data.filterOptions.contents),
        offers: normalizeToFilterOptions(data.filterOptions.offers),
      }
    : {
        platforms: [],
        coaches: [],
        closers: [],
        setters: [],
        contents: [],
        offers: [],
      };

  const filters = [
    {
      key: "content",
      label: "Select Content",
      options: filterOpts.contents,
      value: contents,
      onChange: (v: string | string[]) => setContents(v as string[]),
      multiple: true,
    },
    {
      key: "platform",
      label: "Select Platforms",
      options: filterOpts.platforms,
      value: platforms,
      onChange: (v: string | string[]) => setPlatforms(v as string[]),
      multiple: true,
    },
    {
      key: "coach",
      label: "Select Coaches",
      options: filterOpts.coaches,
      value: coaches,
      onChange: (v: string | string[]) => setCoaches(v as string[]),
      multiple: true,
    },
    {
      key: "closer",
      label: "Select Closers",
      options: filterOpts.closers,
      value: closers,
      onChange: (v: string | string[]) => setClosers(v as string[]),
      multiple: true,
    },
    {
      key: "setter",
      label: "Select Setters",
      options: filterOpts.setters,
      value: setters,
      onChange: (v: string | string[]) => setSetters(v as string[]),
      multiple: true,
    },
    ...(offer === "all" && filterOpts.offers.length > 0
      ? [{
          key: "offers",
          label: "Select Offers",
          options: filterOpts.offers,
          value: offers.filter((o: string) => o !== "all"),
          onChange: (v: string | string[]) => setOffers(["all", ...(v as string[])]),
          multiple: true,
        }]
      : []),
  ];

  // Clear all filters (date range is managed globally by the header picker)
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Inject keyframes
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes shimmer {
        0% { background-position: 200% 0; }
        100% { background-position: -200% 0; }
      }
      @keyframes float {
        0%, 100% { transform: translateY(0px); }
        50% { transform: translateY(-6px); }
      }
      @keyframes pulse-glow {
        0%, 100% { opacity: 0.5; }
        50% { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Build DashboardData for context
  const dashboardData: DashboardData = {
    page: "leaderboard",
    metrics: {
      totalClosers: data?.topClosers?.length ?? 0,
      totalSetters: data?.topSetters?.length ?? 0,
    },
    chartData: {},
    tableData: {
      topClosers: (sortedClosers as unknown as unknown[]) ?? [],
      topSetters: (sortedSetters as unknown as unknown[]) ?? [],
    },
    filterOptions: {
      platforms: filterOpts.platforms as unknown as unknown[],
      coaches: filterOpts.coaches as unknown as unknown[],
      closers: filterOpts.closers as unknown as unknown[],
      setters: filterOpts.setters as unknown as unknown[],
      contents: filterOpts.contents as unknown as unknown[],
      offers: filterOpts.offers as unknown as unknown[],
    },
    rawData: (data as unknown as Record<string, unknown>) ?? {},
    isLoading,
    refetch: fetchData,
  };

  // Loading state
  if (isLoading && !data) {
    return <LoadingSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <CrownIcon className="w-12 h-12 md:w-20 md:h-20 mx-auto text-yellow-500 mb-4 md:mb-6" />
          <h1
            className="text-3xl md:text-5xl font-black tracking-tight mb-2 md:mb-3"
            style={{
              background: "linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            LEADERBOARD
          </h1>
          <p className="text-muted-foreground">Top performers tracked in real-time</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center">
          <p className="text-red-400 font-medium mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-6 py-2.5 bg-red-500 text-white rounded-lg font-medium hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Custom leaderboard header with animated crown, rankings grid, and filter bar
  const leaderboardHeader = (
    <div className="w-full min-w-0">
      {/* Header */}
      <div className="text-center py-4 sm:py-6 md:py-12">
        <div
          className="relative inline-block"
          style={{ animation: "float 4s ease-in-out infinite" }}
        >
          <CrownIcon className="w-10 h-10 sm:w-12 sm:h-12 md:w-20 md:h-20 mx-auto text-yellow-500 drop-shadow-lg" />
          {/* Glow behind crown */}
          <div
            className="absolute inset-0 -z-10 blur-2xl opacity-50"
            style={{
              background: "radial-gradient(circle, #FFD700 0%, transparent 70%)",
            }}
          />
        </div>

        <h1
          className="text-2xl sm:text-3xl md:text-5xl font-black tracking-tight mt-3 sm:mt-4 md:mt-6 mb-1 sm:mb-2 md:mb-3"
          style={{
            background: "linear-gradient(135deg, var(--brand-gradient-start, #F04911) 0%, var(--brand-gradient-end, #D03D0E) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          LEADERBOARD
        </h1>
        <p className="text-muted-foreground text-xs sm:text-sm md:text-lg">Top performers tracked in real-time</p>
      </div>

      {/* Rankings Grid */}
      <div className="grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-2">
        {/* Top Closers */}
        <div className="rounded-2xl border border-border/30 bg-card/30 backdrop-blur-sm overflow-hidden">
          <SectionHeader
            icon={
              <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z" />
              </svg>
            }
            title="Top Closers"
            iconColor="#FFD700"
          >
            <MetricButton label="Cash" active={closerMetric === "cash"} onClick={() => setCloserMetric("cash")} />
            <MetricButton label="AOV" active={closerMetric === "aov"} onClick={() => setCloserMetric("aov")} />
            <MetricButton label="$/Call" active={closerMetric === "cashPerCall"} onClick={() => setCloserMetric("cashPerCall")} />
            <MetricButton label="Close %" active={closerMetric === "closeRate"} onClick={() => setCloserMetric("closeRate")} />
          </SectionHeader>

          <div className="p-4 space-y-2">
            {sortedClosers.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No closers found for the selected filters</p>
            ) : (
              sortedClosers.map((closer, index) => (
                <RankingCard
                  key={closer.id}
                  rank={index + 1}
                  name={closer.closerName}
                  subtitle={getCloserSubtitle(closer)}
                  primaryValue={getCloserPrimaryValue(closer)}
                  secondaryValue={formatCurrency(closer.totalCashCollected) + " total"}
                  index={index}
                  isVisible={isVisible}
                  onClick={() => setSelectedCloserId(closer.id)}
                  callToAction="View offer mix"
                />
              ))
            )}
          </div>
        </div>

        {/* Top Setters */}
        <div className="rounded-2xl border border-border/30 bg-card/30 backdrop-blur-sm overflow-hidden">
          <SectionHeader
            icon={
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2L9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24l-7.19-.61L12 2z" />
              </svg>
            }
            title="Top Setters"
            iconColor="#9CA3AF"
          >
            <MetricButton label="Cash" active={setterMetric === "cash"} onClick={() => setSetterMetric("cash")} />
            <MetricButton label="$/Call" active={setterMetric === "cashPerCall"} onClick={() => setSetterMetric("cashPerCall")} />
            <MetricButton label="Calls" active={setterMetric === "calls"} onClick={() => setSetterMetric("calls")} />
            <MetricButton label="Show %" active={setterMetric === "showRate"} onClick={() => setSetterMetric("showRate")} />
          </SectionHeader>

          <div className="p-4 space-y-2">
            {sortedSetters.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">No setters found for the selected filters</p>
            ) : (
              sortedSetters.map((setter, index) => (
                <RankingCard
                  key={setter.id}
                  rank={index + 1}
                  name={setter.setterName}
                  subtitle={getSetterSubtitle(setter)}
                  primaryValue={getSetterPrimaryValue(setter)}
                  secondaryValue={formatCurrency(setter.totalCashCollected) + " total"}
                  index={index}
                  isVisible={isVisible}
                  onClick={() => setSelectedSetterId(setter.id)}
                  callToAction="View offer mix"
                />
              ))
            )}
          </div>
        </div>
      </div>

      <CashByOfferModal
        summary={selectedCashByOffer}
        open={Boolean(selectedCashByOffer)}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedCloserId(null);
            setSelectedSetterId(null);
          }
        }}
      />
    </div>
  );

  return (
    <DashboardDataProvider value={dashboardData}>
      <DashboardShell
        page="leaderboard"
        defaultLayout={LEADERBOARD_DEFAULT}
        allowEditing={false}
        filterBar={
          <FilterBar
            showDateFieldToggle
            dateField={dateField as "timestamp" | "dateCallWasTaken"}
            onDateFieldChange={(v) => setDateField(v)}
            filters={filters}
            onClearAll={handleClearAll}
          />
        }
        header={leaderboardHeader}
      />
    </DashboardDataProvider>
  );
}
